import { MouseEvent, ReactNode, forwardRef } from 'react';
import { Link as RouterLink, LinkProps } from 'react-router-dom';
// @mui
import { Box, ButtonBase } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
//
import { ProductBrand } from '../../../data/products';
import { FONT, STAGE, TYPE } from '../../../theme/stage';
import { CSS_EASE, DURATION } from '../motion';

// ----------------------------------------------------------------------

type Variant = 'pill' | 'brand' | 'link' | 'quiet' | 'product';

type Icon = 'external' | 'down' | 'chevron' | 'forward' | 'up' | 'none';

type Props = {
  href: string;
  icon?: Icon;
  ariaLabel?: string;
  logo?: { src: string };
  onClick?: (event: MouseEvent) => void;
  sx?: SxProps<Theme>;
  children: ReactNode;
} & (
  | { variant: 'brand'; brand: ProductBrand }
  | { variant: Exclude<Variant, 'brand'>; brand?: never }
);

const ICONS = {
  external: NorthEastRounded,
  down: ArrowDownwardRounded,
  chevron: ChevronRightRounded,
  forward: ArrowForwardRounded,
  up: ArrowUpwardRounded,
};

const ICON_SIZE: Record<Variant, number> = {
  pill: 18,
  brand: 18,
  link: 20,
  quiet: 14,
  product: 16,
};

const NUDGE: Partial<Record<Icon, string>> = {
  external: 'translate(2px, -2px)',
  chevron: 'translateX(2px)',
  forward: 'translateX(2px)',
};

const HOVER = '@media (hover: hover)';

const HOVER_MOTION = '@media (hover: hover) and (prefers-reduced-motion: no-preference)';

const MOTION = '@media (prefers-reduced-motion: no-preference)';

const RING = { outline: `2px solid ${STAGE.focus}`, outlineOffset: '3px' };

const VARIANTS: Record<Exclude<Variant, 'brand'>, SystemStyleObject<Theme>> = {
  pill: {
    ...TYPE.button,
    gap: '8px',
    height: { xs: 50, sm: 52 },
    px: '26px',
    borderRadius: '999px',
    bgcolor: STAGE.ink,
    color: STAGE.ground,
    [HOVER]: { '&:hover': { bgcolor: STAGE.pillHover } },
  },
  link: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: { xs: 16, sm: 17 },
    lineHeight: 1,
    gap: '2px',
    minWidth: 44,
    minHeight: 44,
    color: STAGE.ink2,
    '& .ActionLink-label': { textUnderlineOffset: '4px' },
    [HOVER]: { '&:hover .ActionLink-label': { textDecoration: 'underline' } },
  },
  quiet: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: 15,
    lineHeight: 1,
    gap: '4px',
    minWidth: 44,
    minHeight: 44,
    color: STAGE.mute,
    '& .ActionLink-label': { textUnderlineOffset: '3px' },
    [HOVER]: {
      '&:hover': { color: STAGE.ink, '& .ActionLink-label': { textDecoration: 'underline' } },
    },
  },
  product: {
    fontFamily: FONT.sans,
    fontWeight: 500,
    fontSize: 17,
    lineHeight: 1,
    gap: '12px',
    height: 52,
    p: '0 20px 0 8px',
    borderRadius: '999px',
    bgcolor: STAGE.surface,
    border: `1px solid ${STAGE.line}`,
    color: STAGE.ink,
    '& .ActionLink-icon': { color: STAGE.mute },
    [HOVER]: {
      '&:hover': { bgcolor: STAGE.surface2, borderColor: STAGE.lineStrong },
    },
  },
};

const RouteLink = forwardRef<HTMLAnchorElement, Omit<LinkProps, 'to'> & { href: string }>(
  function RouteLink({ href, ...other }, ref) {
    return <RouterLink ref={ref} to={href} {...other} />;
  }
);

function brandSx({ main, hover, onMain }: ProductBrand): SystemStyleObject<Theme> {
  return {
    ...TYPE.button,
    fontSize: 16,
    gap: '8px',
    height: 50,
    px: '24px',
    borderRadius: '999px',
    bgcolor: main,
    color: onMain,
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.12)',
    '@container product (min-width: 600px)': { height: 52, fontSize: 17 },
    [HOVER]: { '&:hover': { bgcolor: hover } },
  };
}

export default function ActionLink(props: Props) {
  const {
    href,
    variant,
    icon = /^(#|\/|mailto:)/.test(href) ? 'none' : 'external',
    ariaLabel,
    logo,
    onClick,
    sx,
    children,
  } = props;

  const Icon = icon === 'none' ? null : ICONS[icon];

  const nudge = NUDGE[icon];

  return (
    <ButtonBase
      disableRipple
      LinkComponent={href.startsWith('/') ? RouteLink : 'a'}
      href={href}
      aria-label={ariaLabel}
      onClick={onClick}
      sx={[
        {
          font: 'inherit',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          transition: ['background-color', 'color', 'border-color', 'transform']
            .map((property) => `${property} ${DURATION.hover}ms ${CSS_EASE.outExpo}`)
            .join(', '),
          '& .ActionLink-icon': {
            fontSize: ICON_SIZE[variant],
            transition: `transform ${DURATION.hover}ms ${CSS_EASE.outExpo}`,
          },
          '&.Mui-focusVisible, &:focus-visible': RING,
          [MOTION]: {
            '&:active': { transform: 'scale(.98)', transitionDuration: `${DURATION.press}ms` },
          },
          ...(nudge && { [HOVER_MOTION]: { '&:hover .ActionLink-icon': { transform: nudge } } }),
        },
        props.variant === 'brand' ? brandSx(props.brand) : VARIANTS[props.variant],
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {logo && (
        <Box
          component="span"
          sx={{
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: '999px',
            bgcolor: '#FFFFFF',
          }}
        >
          <Box component="img" src={logo.src} alt="" width={24} height={24} />
        </Box>
      )}
      <span className="ActionLink-label">{children}</span>
      {Icon && <Icon aria-hidden className="ActionLink-icon" />}
    </ButtonBase>
  );
}
